import { createEffect, createMemo, createSignal, onCleanup, onMount, ParentComponent } from 'solid-js'
import { useNavigate } from '@solidjs/router'
import { useBreakpoint } from './useBreakpoint'
import { TopBar } from './TopBar'
import { Sidebar } from './Sidebar'
import { SidebarToggle } from './Sidebar'
import { MobileDrawer } from './MobileDrawer'
import { BottomTabBar } from './BottomTabBar'
import { MobileFreeTokensBar } from './MobileFreeTokensBar'
import { subscribe, unsubscribe } from '/web/store/socket'
import { toastStore, userStore } from '/web/store'
import { EVENTS, events } from '/web/emitter'
import './nav.css'

export const AppShell: ParentComponent = (props) => {
  const bp = useBreakpoint()
  const navigate = useNavigate()

  // Redirect home whenever the user logs out, from any logout button in the app
  // (the loggedOut event fires only on an explicit logout, not on auth state flips).
  const onLoggedOut = () => navigate('/')
  const [drawerOpen, setDrawerOpen] = createSignal(false)
  const [sidebarOverride, setSidebarOverride] = createSignal<'expanded' | 'collapsed' | null>(null)

  const tokensChangedValidator = { userId: 'string', tokens: 'number', hasClaimedInitialTokens: 'boolean?' } as const

  const handleTokensChanged = (body: { userId: string; tokens: number; hasClaimedInitialTokens?: boolean }) => {
    const user = userStore.getState()
    if (user.profile) {
      userStore.setState({
        profile: {
          ...user.profile,
          totalAvailableTokens: body.tokens,
          hasClaimedInitialTokens: body.hasClaimedInitialTokens ?? user.profile.hasClaimedInitialTokens,
        },
      })
    }
  }

  const ownUserId = () => userStore.getState().profile?.userId

  const rewardProgressValidator = { userId: 'string', lifetime: 'number' } as const
  const handleRewardProgress = (body: { userId: string; lifetime: number }) => {
    if (body.userId !== ownUserId()) return
    const user = userStore.getState()
    if (user.profile) {
      userStore.setState({ profile: { ...user.profile, lifetimeTokensSpent: body.lifetime } })
    }
  }

  const levelUpValidator = {
    userId: 'string',
    level: 'number',
    name: 'string',
    bonus: 'number',
    lifetime: 'number',
  } as const
  const handleLevelUp = (body: { userId: string; name: string; bonus: number }) => {
    if (body.userId !== ownUserId()) return
    toastStore.success(`Level Up! ${body.name} Achieved — +${body.bonus} bonus tokens credited`)
  }

  // Load AIFans subscriptions once whenever the user is logged in, so premium state
  // (Member pill in the top bar, Tokens tab in the bottom nav) is available app-wide —
  // not only on pages that fetch it (Model/Subscriptions). Gated on a memo of `loggedIn`
  // so it fires only when auth state flips, not on every store update.
  const loggedIn = createMemo(() => userStore().loggedIn)
  createEffect(() => {
    if (loggedIn()) {
      userStore.loadAIFansSubscriptions()
    }
  })

  onMount(() => {
    subscribe('profile-tokens-changed', tokensChangedValidator, handleTokensChanged)
    subscribe('reward-progress', rewardProgressValidator, handleRewardProgress)
    subscribe('reward-level-up', levelUpValidator, handleLevelUp)
    events.on(EVENTS.loggedOut, onLoggedOut)
  })

  onCleanup(() => {
    unsubscribe('profile-tokens-changed', handleTokensChanged)
    unsubscribe('reward-progress', handleRewardProgress)
    unsubscribe('reward-level-up', handleLevelUp)
    events.removeListener(EVENTS.loggedOut, onLoggedOut)
  })

  const sidebarClass = () => {
    const override = sidebarOverride()
    if (bp() === 'desktop') {
      return override === 'collapsed' ? 'cf-sidebar-collapsed' : ''
    }
    if (bp() === 'tablet') {
      return override === 'expanded' ? 'cf-sidebar-expanded' : ''
    }
    return ''
  }

  const toggleSidebar = () => {
    const override = sidebarOverride()
    if (bp() === 'desktop') {
      setSidebarOverride(override === 'collapsed' ? null : 'collapsed')
    } else if (bp() === 'tablet') {
      setSidebarOverride(override === 'expanded' ? null : 'expanded')
    }
  }

  const sidebarCollapsed = () => {
    if (bp() === 'desktop') return sidebarOverride() === 'collapsed'
    if (bp() === 'tablet') return sidebarOverride() !== 'expanded'
    return true
  }

  return (
    <div class={`cf-app ${sidebarClass()}`}>
      <TopBar onOpenDrawer={() => setDrawerOpen(true)} />
      <Sidebar collapsed={sidebarCollapsed()} onToggle={toggleSidebar} />
      <SidebarToggle collapsed={sidebarCollapsed()} onToggle={toggleSidebar} />
      <MobileDrawer open={drawerOpen()} onClose={() => setDrawerOpen(false)} />
      <div class="cf-main">{props.children}</div>
      <BottomTabBar />
      {/* #216: mobile Free Tokens bar — global (was home-only in HomeSpotlight). */}
      <MobileFreeTokensBar />
    </div>
  )
}
